import { hydrateKendoForm } from "../hydrator.js";
import type {
  ConsiderationSelection,
  ConsiderationOptionDataItem,
  HydratorConfig,
  HydrationOption,
  TaskGlobalHandlers,
  TaskResponse,
} from "../types/index.js";
import { createHydratorConfig, executeScripts, setUrl } from "../utils.js";

const commonRiskColorClasses = [
  "text-bg-success",
  "text-bg-warning",
  "text-bg-danger",
  "text-bg-critical",
];

function getExistingConsiderationOptions(selector: string) {
  const widget = $(selector).data("kendoMultiSelect") as
    | kendo.ui.MultiSelect
    | undefined;

  if (!widget) {
    return new Map<string, HydrationOption>();
  }

  const textField = widget.options.dataTextField ?? "Text";
  const valueField = widget.options.dataValueField ?? "Value";
  const options = new Map<string, HydrationOption>();

  for (const item of (widget.dataSource.data() as unknown as
    | Record<string, unknown>[]
    | undefined) ?? []) {
    const optionValue = item[valueField] ?? item.Value ?? item.Key ?? item.Name;
    const optionText =
      item[textField] ?? item.Text ?? item.Name ?? item.Key ?? optionValue;

    if (!optionValue || !optionText) {
      continue;
    }

    options.set(`${optionValue}`, {
      Text: `${optionText}`,
      Value: `${optionValue}`,
    });
  }

  return options;
}

function normalizeConsiderationSelections(
  selector: string,
  value:
    | TaskResponse["ProcessConsiderations"]
    | TaskResponse["PersonalConsiderations"],
): HydrationOption[] {
  if (!Array.isArray(value)) {
    return [];
  }

  const existingOptions = getExistingConsiderationOptions(selector);

  return value.flatMap((item) => {
    if (typeof item === "string") {
      return [existingOptions.get(item) ?? { Text: item, Value: item }];
    }

    const selection = item as ConsiderationSelection;
    const optionValue = selection.Key ?? selection.Text ?? selection.Name;
    const existingOption = optionValue
      ? existingOptions.get(`${optionValue}`)
      : undefined;
    const optionText =
      selection.Text ?? selection.Name ?? existingOption?.Text ?? selection.Key;

    if (!optionValue || !optionText) {
      return [];
    }

    return [{ Text: `${optionText}`, Value: `${optionValue}` }];
  });
}

function getConsiderationOptions(
  selector: string,
  value:
    | TaskResponse["ProcessConsiderations"]
    | TaskResponse["PersonalConsiderations"],
) {
  const optionsByValue = getExistingConsiderationOptions(selector);

  for (const option of normalizeConsiderationSelections(selector, value)) {
    optionsByValue.set(option.Value, option);
  }

  return Array.from(optionsByValue.values());
}

const taskFormHydrator: HydratorConfig<TaskResponse> = createHydratorConfig({
  fields: [
    {
      property: "Key",
      selector: "input[name='Key']",
      type: "hidden",
      fallback: "",
    },
    {
      property: "Name",
      selector: "h4 p",
      type: "content",
      fallback: "New Task",
      transform: (value) =>
        typeof value === "string" && value ? value : "New Task",
    },
    { property: "Name", selector: "#TaskName", type: "text", fallback: "" },
    {
      property: "ProcessSafetyHazards",
      selector: "#ProcessSafetyHazards",
      type: "text",
      fallback: "",
    },
    {
      property: "IsField",
      selector: "#IsField",
      type: "boolean",
      fallback: false,
    },
    {
      property: "IsInHouse",
      selector: "#IsInHouse",
      type: "boolean",
      fallback: false,
    },
    {
      property: "PersonalSafetyHazards",
      selector: "#PersonalSafetyHazards",
      type: "text",
      fallback: "",
    },
    {
      property: "ProcessRisk",
      selector: "#ProcessRisk",
      type: "text",
      fallback: "",
    },
    {
      property: "PersonalRisk",
      selector: "#PersonalRisk",
      type: "text",
      fallback: "",
    },
    {
      property: "ProcessControlsOrMitigations",
      selector: "#ControlsOrMitigations",
      type: "text",
      fallback: "",
    },
    {
      property: "ProcessLikelihood",
      selector: "#ProcessLikelihood",
      type: "numeric",
      fallback: 0,
    },
    {
      property: "ProcessSeverity",
      selector: "#ProcessSeverity",
      type: "numeric",
      fallback: 0,
    },
    {
      property: "ProcessRiskScore",
      selector: "#ProcessRiskScore",
      type: "numeric",
      fallback: 0,
    },
    {
      property: "ProcessConsiderations",
      selector: "#ProcessConsiderations",
      type: "multiselect",
      fallback: [],
      transform: (value, response) =>
        normalizeConsiderationSelections(
          "#ProcessConsiderations",
          (value ??
            response.ProcessConsiderations) as TaskResponse["ProcessConsiderations"],
        ).map((item) => item.Value),
      getOptions: (value, response) =>
        getConsiderationOptions(
          "#ProcessConsiderations",
          (value ??
            response.ProcessConsiderations) as TaskResponse["ProcessConsiderations"],
        ),
    },
    {
      property: "PersonalConsiderations",
      selector: "#PersonalConsiderations",
      type: "multiselect",
      fallback: [],
      transform: (value, response) =>
        normalizeConsiderationSelections(
          "#PersonalConsiderations",
          (value ??
            response.PersonalConsiderations) as TaskResponse["PersonalConsiderations"],
        ).map((item) => item.Value),
      getOptions: (value, response) =>
        getConsiderationOptions(
          "#PersonalConsiderations",
          (value ??
            response.PersonalConsiderations) as TaskResponse["PersonalConsiderations"],
        ),
    },
    {
      property: "ProjectPhases",
      selector: "#ProjectPhases",
      type: "multiselect",
      fallback: [],
      transform: (value) =>
        Array.isArray(value) ? value.map((item) => `${item}`) : [],
    },
  ],
  afterHydrate: (task) => {
    const key = task.Key ?? "";
    const isNew = task.New ?? false;

    const deleteButton = $("#DeleteTaskButton").data("kendoButton") as
      | kendo.ui.Button
      | undefined;
    if (deleteButton) {
      deleteButton.enable(!(task.New ?? false));
    }

    const deleteButtonElement = document.getElementById("DeleteTaskButton");
    if (deleteButtonElement) {
      deleteButtonElement.setAttribute("onclick", `deleteTask('${key}')`);
    }

    calculateRisk({
      sender: $("#PersonalLikelihood").data("kendoNumericTextBox")!,
      isDefaultPrevented: () => false,
      preventDefault: () => {},
    });

    calculateRisk({
      sender: $("#ProcessLikelihood").data("kendoNumericTextBox")!,
      isDefaultPrevented: () => false,
      preventDefault: () => {},
    });

    if (isNew) {
      setUrl();
    } else if (key) {
      setUrl(key);
    }
  },
  formId: "TaskDetailsForm",
});

function taskRadioChanged(event: kendo.ui.RadioButtonChangeEvent) {
  const radio = event.sender;

  const otherRadio = $(`#${radio!.element.attr("data-other")!}`).data(
    "kendoRadioButton",
  ) as kendo.ui.RadioButton;

  if (!radio) return;

  const value = radio.check();

  if (!value) return;

  otherRadio.check(false);
}

//@ts-ignore
function appendConsiderationDictionary(
  parameters: URLSearchParams,
  fieldName: string,
  multiSelectSelector: string,
  optionsSelector: string,
) {
  const considerationMultiSelect = $(multiSelectSelector).data(
    "kendoMultiSelect",
  ) as kendo.ui.MultiSelect | undefined;
  const optionMultiSelect = $(optionsSelector).data("kendoMultiSelect") as
    | kendo.ui.MultiSelect
    | undefined;

  if (!considerationMultiSelect) {
    return;
  }

  const selectedConsiderations = (
    (considerationMultiSelect.value() as string[]) ?? []
  ).map((value) => `${value}`);

  if (selectedConsiderations.length === 0) {
    return;
  }

  const selectedOptions = (optionMultiSelect?.dataItems() ?? []) as
    | ConsiderationOptionDataItem[]
    | undefined;
  const optionValuesByConsideration = new Map<string, string[]>();

  for (const option of selectedOptions ?? []) {
    const considerationKey = option.ConsiderationKey;
    const optionValue = option.Value;

    if (!considerationKey || !optionValue) {
      continue;
    }

    const values = optionValuesByConsideration.get(considerationKey) ?? [];
    values.push(optionValue);
    optionValuesByConsideration.set(considerationKey, values);
  }

  selectedConsiderations.forEach((considerationKey, index) => {
    parameters.append(`${fieldName}[${index}].Key`, considerationKey);

    const optionValues =
      optionValuesByConsideration.get(considerationKey) ?? [];
    optionValues.forEach((optionValue, optionIndex) => {
      parameters.append(
        `${fieldName}[${index}].Value[${optionIndex}]`,
        optionValue,
      );
    });
  });
}

function getBooleanFieldValue(selector: string) {
  const radioButton = $(selector).data("kendoRadioButton") as
    | kendo.ui.RadioButton
    | undefined;

  if (radioButton) {
    return radioButton.check();
  }

  const checkBox = $(selector).data("kendoCheckBox") as
    | kendo.ui.CheckBox
    | undefined;

  if (checkBox) {
    return checkBox.check();
  }

  const element = document.querySelector(selector) as HTMLInputElement | null;
  return element?.checked ?? false;
}

function appendBooleanField(
  parameters: URLSearchParams,
  fieldName: string,
  selector: string,
) {
  parameters.delete(fieldName);
  parameters.append(fieldName, `${getBooleanFieldValue(selector)}`);
}

function serializeTaskForm(form: HTMLFormElement) {
  const parameters = new URLSearchParams(
    Array.from(new FormData(form).entries()).flatMap(([key, value]) => {
      if (typeof value !== "string") {
        return [];
      }

      // if (
      //   key === "ProcessConsiderations" ||
      //   key === "ProcessConsiderationsOptions" ||
      //   key === "PersonalConsiderations" ||
      //   key === "PersonalConsiderationsOptions"
      // ) {
      //   return [];
      // }

      return [[key, value]];
    }),
  );

  // appendConsiderationDictionary(
  //   parameters,
  //   "ProcessConsiderations",
  //   "#ProcessConsiderations",
  //   "#ProcessConsiderationsOptions",
  // );
  // appendConsiderationDictionary(
  //   parameters,
  //   "PersonalConsiderations",
  //   "#PersonalConsiderations",
  //   "#PersonalConsiderationsOptions",
  // );

  appendBooleanField(parameters, "IsField", "#IsField");
  appendBooleanField(parameters, "IsInHouse", "#IsInHouse");

  return parameters.toString();
}

function hydrateTaskForm(task: TaskResponse) {
  const form = taskFormHydrator.getForm();
  if (!form) {
    return;
  }

  if (
    $("#no-task-selected-header") &&
    $("#task-selected-header").hasClass("d-none")
  ) {
    $("#no-task-selected-header").toggleClass("d-none");

    $("#task-selected-header").removeClass("d-none");
  }

  $("#task-selected-header p").text(task.Name!);

  hydrateKendoForm(task, taskFormHydrator);
}

function handleTaskResponse(response: unknown) {
  if (typeof response === "string") {
    replaceTaskDetailsForm(response);
    return;
  }

  if (response && typeof response === "object") {
    hydrateTaskForm(response as TaskResponse);
  }
}

function replaceTaskDetailsForm(markup: string) {
  const currentForm = taskFormHydrator.getForm();

  if (!currentForm) {
    return;
  }

  const template = document.createElement("template");
  template.innerHTML = markup.trim();

  const nextForm = template.content.querySelector(
    "#TaskDetailsForm",
  ) as HTMLFormElement | null;

  if (!nextForm) {
    return;
  }

  const scripts = Array.from(
    template.content.querySelectorAll("script"),
  ) as HTMLScriptElement[];

  for (const script of scripts) {
    script.remove();
  }

  kendo.destroy(currentForm);
  currentForm.replaceWith(nextForm);

  if (scripts.length > 0) {
    executeScripts(scripts);
  } else {
    kendo.init(nextForm);
  }
}

function calculateTotalRisk(finalRiskElement: kendo.ui.NumericTextBox) {
  const calculateRiskElements = $("[data-contributes-to-total-risk]")
    .toArray()
    .map((element) => $(element).data("kendoNumericTextBox")!);

  //I want to get the element with the highest value.
  const totalRisk = Math.max(
    ...calculateRiskElements.map((element) => element.value()),
  );

  finalRiskElement.value(totalRisk);
}

function loadRiskInitial() {
  calculateRisk({
    sender: $("#PersonalLikelihood").data("kendoNumericTextBox")!,
    isDefaultPrevented: () => false,
    preventDefault: () => {},
  });

  calculateRisk({
    sender: $("#ProcessLikelihood").data("kendoNumericTextBox")!,
    isDefaultPrevented: () => false,
    preventDefault: () => {},
  });
}

function getOptionMultiSelectForRiskInput(element: JQuery) {
  const name = element.attr("name") ?? "";

  if (name.startsWith("Personal")) {
    return $("#PersonalConsiderationsOptions").data("kendoMultiSelect") as
      | kendo.ui.MultiSelect
      | undefined;
  }

  if (name.startsWith("Process")) {
    return $("#ProcessConsiderationsOptions").data("kendoMultiSelect") as
      | kendo.ui.MultiSelect
      | undefined;
  }

  return undefined;
}

function getRiskAdderForInput(
  element: JQuery,
  option: ConsiderationOptionDataItem,
) {
  const name = element.attr("name") ?? "";

  if (name.endsWith("Likelihood")) {
    return option?.RiskLikelihoodAdder ?? 0;
  }

  if (name.endsWith("Severity")) {
    return option?.RiskSeverityAdder ?? 0;
  }

  return 0;
}

function getTotalRiskAdderForInput(element: JQuery) {
  const optionMultiSelect = getOptionMultiSelectForRiskInput(element);
  const selectedOptions = (optionMultiSelect?.dataItems() ??
    []) as ConsiderationOptionDataItem[];

  return selectedOptions.reduce((total, option) => {
    return total + getRiskAdderForInput(element, option);
  }, 0);
}

function getAdjustedRiskValue(sender: kendo.ui.NumericTextBox) {
  const element = sender.element;

  return (sender.value() ?? 0) + getTotalRiskAdderForInput(element);
}

function calculateRiskInitial(sender: kendo.ui.NumericTextBox) {
  const element = sender.element;
  const receiver = getAdjustedRiskValue(sender);
  const pairInput = $(`#${element.attr("data-pair")}`).data(
    "kendoNumericTextBox",
  ) as kendo.ui.NumericTextBox | undefined;
  const pair = pairInput ? getAdjustedRiskValue(pairInput) : 0;
  const totalRisk = receiver * pair;

  $(`#${element.attr("data-total")}`)
    .data("kendoNumericTextBox")!
    .value(totalRisk);

  calculateTotalRisk(
    $(`#${element.attr("data-final")}`).data("kendoNumericTextBox")!,
  );

  const badgeElement = $(`#${element.attr("data-badge")}`);

  if (totalRisk >= 10) {
    badgeElement!
      .removeClass(commonRiskColorClasses.join(" "))
      .addClass("text-bg-danger")
      .text("High");
  } else if (totalRisk >= 8) {
    badgeElement!
      .removeClass(commonRiskColorClasses.join(" "))
      .addClass("text-bg-warning")
      .text("Medium");
  } else if (totalRisk >= 4) {
    badgeElement!
      .removeClass(commonRiskColorClasses.join(" "))
      .addClass("text-bg-success")
      .text("Low");
  } else {
    badgeElement!
      .removeClass(commonRiskColorClasses.join(" "))
      .addClass("text-bg-secondary")
      .text("No Risk");
  }
}

function calculateRisk(input: kendo.ui.NumericTextBoxChangeEvent) {
  const sender = input.sender;

  calculateRiskInitial(sender);
}

function saveTask() {
  const form = taskFormHydrator.getForm();
  const notification = $("#popup_notification").data("kendoNotification")!;

  if (!form) {
    notification.show("Task form was not found.", "error");
    return;
  }

  $.ajax({
    url: "Tasks/Save",
    method: "POST",
    data: serializeTaskForm(form),
    success: function (response: unknown) {
      notification.show("Task saved successfully!", "success");

      handleTaskResponse(response);

      $("#TasksGrid").data("kendoGrid")!.dataSource.read();
    },
    error: function () {
      notification.show(
        "An error occurred while saving the task. Please try again.",
        "error",
      );
    },
  });
}

function selectTask(e: HTMLButtonElement) {
  const tr = $(e).closest("tr");
  const grid = $("#TasksGrid").data("kendoGrid")!;
  const tabs = $("#TasksTabs").data("kendoTabStrip")!;

  const dataItem = grid.dataItem(tr);

  const searchParams = new URLSearchParams(window.location.search);
  searchParams.set("rowKey", dataItem.get("Key"));

  const url = `${window.location.protocol}//${window.location.host}${
    window.location.pathname
  }?${searchParams.toString()}`;

  window.history.pushState({ path: url }, "", url);

  $.ajax({
    url: `Tasks/Edit/${dataItem.get("Key")}`,
    method: "GET",
    success: function (data: unknown) {
      handleTaskResponse(data);
      grid.select(tr);
      tabs.select(1);
    },
  });
}

function taskDataBounded() {
  const grid = $("#TasksGrid").data("kendoGrid")!;

  const urlParams = new URLSearchParams(window.location.search);
  const rowKey = urlParams.get("rowKey");

  if (rowKey) {
    const dataItem = grid.dataSource.get(rowKey);
    if (dataItem) {
      const tr = grid.tbody.find(`tr[data-uid="${dataItem.uid}"]`);
      grid.select(tr);
    }
  }
}

function addNewTask() {
  const notification = $("#popup_notification").data("kendoNotification")!;

  $.ajax({
    url: `Tasks/Create`,
    method: "GET",
    success: function (data: unknown) {
      $("#TasksTabs").data("kendoTabStrip")!.select(1);
      handleTaskResponse(data);
    },
    error: function () {
      notification.show("An error occurred. Please try again.", "error");
    },
  });
}

function deleteTask(key: string) {
  const notification = $("#popup_notification").data("kendoNotification")!;
  const tabs = $("#TasksTabs").data("kendoTabStrip")!;

  $.ajax({
    url: `Tasks/Delete/${key}`,
    method: "POST",
    success: function () {
      notification.show("Task deleted successfully!", "success");
      tabs.select(0);
      addNewTask();

      refreshTasksRecords();
    },
    error: function () {
      notification.show(
        "An error occurred while deleting the task. Please try again.",
        "error",
      );
    },
  });
}

function getSelectedProjectPhases() {
  const multiSelect = $("#ProjectPhasesMultiSelect").data("kendoMultiSelect") as
    | kendo.ui.MultiSelect
    | undefined;

  return {
    projectPhases: multiSelect?.value() ?? [],
  };
}

function refreshTasksRecords() {
  const grid = $("#TasksGrid").data("kendoGrid")!;
  grid.dataSource.read();
  grid.refresh();
}

Object.assign(globalThis as typeof globalThis & TaskGlobalHandlers, {
  saveTask,
  calculateRisk,
  selectTask,
  deleteTask,
  addNewTask,
  taskDataBounded,
  refreshTasksRecords,
  getSelectedProjectPhases,
  loadRiskInitial,
  taskRadioChanged,
});
